import { gql, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { OpenInExplorer } from 'src/components/common/openInExplorer/OpenInExplorer';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphqlCodeBlock } from 'src/components/graphqlCodeBlock/GraphqlCodeBlock';
import * as GraphQLTypes from 'src/lib/graphqlTypes/types';

const OperationBodyModalQuery = gql`
  query UI__OperationBodyModalQuery($graphId: ID!, $operationId: ID!) {
    service(id: $graphId) {
      id
      operation(id: $operationId) {
        id
        signature
        truncated
      }
    }
  }
`;

interface Props {
  graphId: string;
  graphVariant: string;
  operationId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const OperationBodyModal = ({
  graphId,
  graphVariant,
  operationId,
  isOpen,
  onClose,
}: Props) => {
  const { data, error, loading } = useQuery<
    GraphQLTypes.UI__OperationBodyModalQuery,
    GraphQLTypes.UI__OperationBodyModalQueryVariables
  >(OperationBodyModalQuery, {
    variables: {
      graphId,
      operationId,
    },
  });
  const operation = data?.service?.operation;

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Operation body</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <Loading />
          ) : operation?.signature ? (
            <>
              {operation.truncated ? (
                <>
                  <ErrorMessage>Operation has been truncated</ErrorMessage>
                  <pre className="break-all bg-transparent">
                    {operation.signature}
                  </pre>
                </>
              ) : (
                <GraphqlCodeBlock
                  className="text-sm"
                  sdl={operation.signature}
                />
              )}
            </>
          ) : (
            <ErrorMessage>
              {error?.message ?? 'Error fetching operation body'}
            </ErrorMessage>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            {!loading && operation?.signature && !operation.truncated ? (
              <OpenInExplorer
                variant="secondary"
                graphId={graphId}
                graphVariant={graphVariant}
                document={operation.signature}
              />
            ) : null}
            <Button variant="primary" type="button" onClick={onClose}>
              Close
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
